import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiDeleteWithResult, apiDownload, apiGet, apiPost, saveBlob } from '../../modules/core/index.js'
import type { ExportBatch, GenerateSummaryInput, PerformanceSummary, PerformanceSummaryListItem } from './types.js'

/** The operator app's reads and writes of the Performance Summaries add-on, every one through the inspected fetch. */

const workerBase = (workerId: string): string => `/workers/${encodeURIComponent(workerId)}/performance-summaries`
const facilityBase = (facilityId: string): string => `/facilities/${encodeURIComponent(facilityId)}/performance-summaries`

export const summariesKey = (workerId: string | null) => ['performance-summaries', workerId] as const
export const exportsKey = (facilityId: string | null) => ['performance-exports', facilityId] as const

export function useWorkerSummaries(workerId: string | null) {
  return useQuery({
    queryKey: summariesKey(workerId),
    queryFn: () => apiGet<PerformanceSummaryListItem[]>(workerBase(workerId as string)),
    enabled: workerId !== null,
  })
}

export function useWorkerSummary(workerId: string, summaryId: string | null) {
  return useQuery({
    queryKey: ['performance-summary', workerId, summaryId],
    queryFn: () => apiGet<PerformanceSummary>(`${workerBase(workerId)}/${encodeURIComponent(summaryId as string)}`),
    enabled: summaryId !== null,
    // A summary never changes once stored.
    staleTime: Infinity,
  })
}

export function useGenerateSummary(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: GenerateSummaryInput) => apiPost<PerformanceSummary>(workerBase(workerId), input),
    onSuccess: (summary) => {
      queryClient.setQueryData(['performance-summary', workerId, summary.summaryId], summary)
      void queryClient.invalidateQueries({ queryKey: summariesKey(workerId) })
    },
  })
}

/** The facility releases a summary to the worker's own app, or withdraws it. */
export function useReleaseSummary(workerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ summaryId, release }: { summaryId: string; release: boolean }) => {
      const path = `${workerBase(workerId)}/${encodeURIComponent(summaryId)}/release`
      return release ? apiPost<PerformanceSummary>(path, {}) : apiDeleteWithResult<PerformanceSummary>(path)
    },
    onSuccess: (summary) => {
      queryClient.setQueryData(['performance-summary', workerId, summary.summaryId], summary)
      void queryClient.invalidateQueries({ queryKey: summariesKey(workerId) })
    },
  })
}

/** Saves one summary's CSV under the name the API gives it. */
export async function downloadSummaryCsv(workerId: string, summaryId: string): Promise<void> {
  const download = await apiDownload(`${workerBase(workerId)}/${encodeURIComponent(summaryId)}?format=csv`)
  saveBlob(download.blob, download.fileName ?? `${workerId}-${summaryId}.csv`)
}

export function useFacilityExports(facilityId: string | null) {
  return useQuery({
    queryKey: exportsKey(facilityId),
    queryFn: () => apiGet<ExportBatch[]>(`${facilityBase(facilityId as string)}/exports`),
    enabled: facilityId !== null,
  })
}

/** What one Export for HR produced: the receipts (one per call the roster took), the rows, and any worker left out. */
export interface HrExportOutcome {
  batchIds: string[]
  rows: number
  failed: string[]
  fileName: string
}

/** More calls than any roster needs (200 workers each); the loop stops here rather than run on a cursor that never ends. */
const MAX_EXPORT_PAGES = 50

/** Every page the API answers ends in a newline; a page is made to, so pages never fuse at the join. */
function withTrailingNewline(text: string): string {
  return text.endsWith('\n') ? text : `${text}\n`
}

class PartialExportError extends Error {
  constructor(page: number, receipts: number, cause: unknown) {
    super(`Page ${page} of the roster could not be exported${receipts > 0 ? `; ${receipts} ${receipts === 1 ? 'receipt was' : 'receipts were'} recorded for the pages before it` : ''}. Nothing was saved. ${cause instanceof Error ? cause.message : ''}`.trim())
    this.name = 'PartialExportError'
  }
}

/**
 * Downloads a facility's merit worksheet for a span as one file. The API
 * answers 200 workers per call and a cursor for the rest, so a larger
 * roster is fetched call by call here and joined under one header before
 * the browser saves it; every call leaves its own receipt. A call that
 * fails partway saves nothing and says which page failed.
 */
export async function exportFacilityCsv(facilityId: string, span: string): Promise<HrExportOutcome> {
  const batchIds: string[] = []
  const failed: string[] = []
  const pages: string[] = []
  let cursor: string | null = null
  let fileName = `${facilityId}-${span}.csv`
  for (let page = 1; ; page += 1) {
    if (page > MAX_EXPORT_PAGES) {
      throw new PartialExportError(page, batchIds.length, new Error('the roster did not end'))
    }
    const path: string = `${facilityBase(facilityId)}/export?span=${encodeURIComponent(span)}${cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`}`
    let download
    try {
      download = await apiDownload(path)
    } catch (error: unknown) {
      throw new PartialExportError(page, batchIds.length, error)
    }
    const text = withTrailingNewline(await download.blob.text())
    // Later pages repeat the header line; one header for the whole file.
    pages.push(pages.length === 0 ? text : text.split('\n').slice(1).join('\n'))
    fileName = download.fileName ?? fileName
    const batchId = download.headers.get('x-export-batch')
    if (batchId) {
      batchIds.push(batchId)
    }
    failed.push(...(download.headers.get('x-export-failed') ?? '').split(',').filter((workerId) => workerId !== ''))
    const next = download.headers.get('x-next-cursor')
    if (next === null || next === cursor) {
      break
    }
    cursor = next
  }
  const csv = pages.join('')
  saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName)
  // A facility export carries no free text with line breaks (no notes), so one line is one worker.
  return { batchIds, rows: Math.max(0, csv.trimEnd().split('\n').length - 1), failed, fileName }
}

export function useExportForHr(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (span: string) => exportFacilityCsv(facilityId, span),
    // Receipts are written per page, so a failure partway has still left some: refresh either way.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: exportsKey(facilityId) })
      void queryClient.invalidateQueries({ queryKey: ['performance-summaries'] })
    },
  })
}
